import {
  Link,
  CustomSelector,
  ECustomSelector,
  Typography,
  type BaseType,
  SelectModal,
  ESelectModalType,
} from '@globalmed/atlas-ui';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './LicenseField.module.css';
import { Form } from '@/components/Form';
import { useLanguageContext } from '@/features/LanguageContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useListStore } from '@/stores/list';
import { type TLicenseLocation } from '@/types';
import type { TListCountriesItemCamel } from '@/types/list';

interface IFormData {
  licenseLocations: TLicenseLocation[];
}

export const LicenseField = () => {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    setValue,
    trigger,
  } = useFormContext<IFormData>();

  const countries = useListStore((state) => state.countries);
  const { language } = useLanguageContext();
  const isSmallScreen = useMediaQuery('(max-width: 576px)');
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'licenseLocations',
  });

  const [stateOption, setStateOption] = useState<Record<number, BaseType[]>>(
    {},
  );

  const itemsCountries = useMemo(() => {
    return Object.values(countries).map((country: TListCountriesItemCamel) => {
      const title = country.labels[language] || country.labels['en'];
      return {
        id: country.id,
        title,
      };
    });
  }, [countries, language]);

  const getFieldError = useCallback(
    (index: number, field: keyof TLicenseLocation) => {
      const locationErrors = errors?.licenseLocations;
      return t(locationErrors?.[index]?.[field]?.message ?? '');
    },
    [errors, t],
  );

  const handleCountryChange = useCallback(
    (index: number, countryId: string) => {
      const selectedCountry = Object.values(countries).find(
        (item) => item.id === countryId,
      );

      if (selectedCountry?.subdivisions) {
        const newStateOptions = selectedCountry.subdivisions.map(
          (subdivision) => ({
            id: subdivision.id,
            title: subdivision.name,
          }),
        );

        setStateOption((prev) => ({ ...prev, [index]: newStateOptions }));
        setValue(`licenseLocations.${index}.state`, '');
      } else {
        setValue(`licenseLocations.${index}.state`, '');
        setStateOption((prev) => ({ ...prev, [index]: [] }));
      }
    },
    [countries, setValue],
  );

  useEffect(() => {
    fields.forEach((license, index) => {
      const selectedCountryId = license.country;
      if (selectedCountryId) {
        const selectedCountry = Object.values(countries).find(
          (item) => item.id === selectedCountryId,
        );
        if (selectedCountry?.subdivisions) {
          const newStateOptions = selectedCountry.subdivisions.map(
            (subdivision) => ({
              id: subdivision.id,
              title: subdivision.name,
            }),
          );

          setStateOption((prev) => ({
            ...prev,
            [index]: newStateOptions,
          }));
        }
      }
    });
  }, [countries, fields]);

  return (
    <>
      {fields.map((license, index) => (
        <div key={license.id} className={styles.licenseBlock}>
          <div className={styles.licenseHeader}>
            <Typography
              as="span"
              fontSize="1rem"
              fontWeight="600"
              sx={{ lineHeight: '1.5rem' }}
            >
              {`${t('first-login.licenseNumberLabel')} ${index + 1}`}
            </Typography>
            {index > 0 && (
              <Link
                preIcon="Trash"
                color="black"
                aria-label={`${t('first-login.remove-license')} ${index + 1}`}
                onClick={() => remove(index)}
                size="sm"
              />
            )}
          </div>
          <Form.FieldsRow>
            <Controller
              name={`licenseLocations.${index}.country`}
              control={control}
              render={({ field }) =>
                isSmallScreen ? (
                  <SelectModal
                    type={ESelectModalType.SingleSelect}
                    options={itemsCountries}
                    selected={field.value}
                    onChange={(id) => {
                      field.onChange(id);
                      handleCountryChange(index, String(id));
                    }}
                    labelProps={{
                      label: t('common.country'),
                      required: true,
                    }}
                    error={getFieldError(index, 'country')}
                    placeholder={t('dashboard.select-country')}
                    width="100%"
                  />
                ) : (
                  <CustomSelector
                    type={ECustomSelector.Select}
                    options={itemsCountries}
                    placeholder={t('dashboard.select-country')}
                    onChange={(id) => {
                      field.onChange(id);
                      handleCountryChange(index, id);
                    }}
                    selected={field.value}
                    error={getFieldError(index, 'country')}
                    labelProps={{
                      label: t('common.country'),
                      required: true,
                    }}
                    enableQuickSearch
                    sx={{ width: '100%' }}
                  />
                )
              }
            />
            <Controller
              name={`licenseLocations.${index}.state`}
              control={control}
              render={({ field }) =>
                isSmallScreen ? (
                  <SelectModal
                    type={ESelectModalType.SingleSelect}
                    options={stateOption[index] || []}
                    selected={field.value}
                    onChange={(id) => {
                      setValue(`licenseLocations.${index}.state`, String(id));
                      field.onChange(id);
                      void trigger(`licenseLocations.${index}.state`);
                    }}
                    labelProps={{
                      label: t('common.state'),
                      required: true,
                    }}
                    error={getFieldError(index, 'state')}
                    disabled={
                      !control._formValues.licenseLocations?.[index]?.country
                    }
                    placeholder={t('dashboard.select-state')}
                    width="100%"
                  />
                ) : (
                  <CustomSelector
                    type={ECustomSelector.Select}
                    options={stateOption[index] || []}
                    placeholder={t('dashboard.select-state')}
                    onChange={(id) => {
                      setValue(`licenseLocations.${index}.state`, id);
                      field.onChange(id);
                      void trigger(`licenseLocations.${index}.state`);
                    }}
                    selected={field.value}
                    error={getFieldError(index, 'state')}
                    labelProps={{
                      label: t('common.state'),
                      required: true,
                    }}
                    disabled={
                      !control._formValues.licenseLocations?.[index]?.country
                    }
                    enableQuickSearch
                    sx={{ width: '100%' }}
                  />
                )
              }
            />
          </Form.FieldsRow>
        </div>
      ))}
      <div className={styles.addLicense}>
        <Link
          text={t('common.add-license')}
          onClick={() => append({ country: '', state: '' })}
          color="grey"
          preIcon="Plus"
          size="sm"
          weight="semibold"
        />
      </div>
    </>
  );
};
